import React, { useEffect, useState } from 'react'
import SwimlaneComponent from './Swimlane'
import EditSwimlaneComponent from './EditSwimlane'
import {
    deleteSwimlane,
    updateSwimlane,
} from '../../flux/swimlanes/SwimlaneActions'
import SwimlaneStore, {
    EventsEnum as SwimlaneEvents,
    ISwimlaneBase,
} from '../../flux/swimlanes/SwimlaneStore'
import TaskStore, { EventsEnum as TaskEvents } from '../../flux/tasks/TaskStore'
import { IControlTaskProps } from '../task/ControlTask'
import { moveTask } from '../../flux/tasks/TaskActions'
import DeleteSwimlaneModalComponent from './DeleteSwimlaneModal'

/**
 * Data needed to render a swimlane component
 */
export interface ISwimlaneView extends ISwimlaneBase {
    taskIds: number[]
}

/**
 * Props expected by the ControlSwimlane component.
 */
export interface IControlSwimlaneProps {
    boardId: number
    swimlaneId: number
    canMove: boolean
    onMove: (boardId: number, swimlaneId: number) => void
}

function ControlSwimlaneComponent(props: IControlSwimlaneProps) {
    const [swimlane, setSwimlane] = useState(
        SwimlaneStore.getSwimlane(props.boardId, props.swimlaneId)
    )
    const [isEdit, setIsEdit] = useState(false)
    const [isDelete, setIsDelete] = useState(false)

    /**
     * Converts swimlane state property into ISwimlaneView
     * @returns {ISwimlaneView} ISwimlane as ISwimlaneView
     */
    function getView(): ISwimlaneView {
        return {
            id: swimlane.id,
            name: swimlane.name,
            order: swimlane.order,
            taskIds: swimlane.tasks.map((x) => x.id),
        }
    }

    /**
     * Handles onEdit from Swimlane Component.
     * Sets isEdit state to true.
     */
    function handleEdit() {
        setIsEdit(true)
    }

    /**
     * Handles onCancel from EditSwimlaneComponent.
     * Sets isEdit state to false
     */
    function handleEditCancel() {
        setIsEdit(false)
    }

    /**
     * Handles a save request from the EditSwimlaneComponent
     */
    function handleSave(swimlane: ISwimlaneView) {
        setIsEdit(false)
        updateSwimlane(props.boardId, swimlane)
    }

    /**
     * Handles the request to delete this swimlane
     */
    function handleDeleteRequest() {
        if (swimlane.tasks.length === 0) deleteSwimlane(props.boardId, swimlane)
        else setIsDelete(true)
    }

    /**
     * Invoked when the user clicks on "Cancel"
     * from the delete confirm dialog.
     */
    function handleDeleteCancel() {
        setIsDelete(false)
    }

    /**
     * Invoked when the user clicks on "Confirm"
     * from the delete confirm dialog.
     */
    function handleConfirm() {
        deleteSwimlane(props.boardId, swimlane)
        setIsDelete(false)
    }

    /**
     * Invoked when a task is dropped onto this task.
     * Should instigate the task move action
     * @param event Drag Event
     */
    function handleDrop(event: React.DragEvent) {
        const from = JSON.parse(
            event.dataTransfer.getData('text/plain')
        ) as IControlTaskProps
        moveTask(
            from.boardId,
            from.swimlaneId,
            from.taskId,
            props.boardId,
            props.swimlaneId
        )
    }

    /**
     * Invoked as part of the droppable functionality.
     * prevent default to identify this element as a valid drop target.
     * (may add logic later to restrict this to task drops only)
     * @param event Drag Event
     */
    function handleDragEnter(event: React.DragEvent) {
        event.preventDefault()
    }

    /**
     * Invoked as part of the droppable functionality.
     * prevent default to identify this element as a valid drop target.
     * (may add logic later to restrict this to task drops only)
     * @param event Drag Event
     */
    function handleDragOver(event: React.DragEvent) {
        event.preventDefault()
    }

    useEffect(() => {
        /**
         * Handles the edit event from the swimlane store.
         * If this swimlane is the one which has been edited then update state
         * @param boardId Board ID
         * @param swimlaneId Swimlane ID
         */
        function handleSwimlaneEdit(boardId: number, swimlaneId: number) {
            if (!(boardId === props.boardId && swimlaneId === props.swimlaneId))
                return
            setSwimlane(
                SwimlaneStore.getSwimlane(props.boardId, props.swimlaneId)
            )
        }

        /**
         * Invoked when a task action is dispatched
         * @param boardId Parent board of this task
         * @param swimlaneId Parent Swimlane of this task
         * @param taskId ID of task which has changed
         */
        function handleTaskAction(
            boardId: number,
            swimlaneId: number,
            taskId: number
        ) {
            if (!(boardId === props.boardId && swimlaneId === props.swimlaneId))
                return
            setSwimlane(
                SwimlaneStore.getSwimlane(props.boardId, props.swimlaneId)
            )
        }

        SwimlaneStore.addSwimlaneStoreListener(
            SwimlaneEvents.EDIT,
            handleSwimlaneEdit
        )
        TaskStore.addTaskStoreListener(TaskEvents.ADD, handleTaskAction)
        TaskStore.addTaskStoreListener(TaskEvents.DELETE, handleTaskAction)
        TaskStore.addTaskStoreListener(TaskEvents.INSERT, handleTaskAction)
        TaskStore.addTaskStoreListener(TaskEvents.MOVE, handleTaskAction)
        return () => {
            SwimlaneStore.removeSwimlaneStoreListener(
                SwimlaneEvents.EDIT,
                handleSwimlaneEdit
            )
            TaskStore.removeTaskStoreListener(TaskEvents.ADD, handleTaskAction)
            TaskStore.removeTaskStoreListener(
                TaskEvents.DELETE,
                handleTaskAction
            )
            TaskStore.removeTaskStoreListener(
                TaskEvents.INSERT,
                handleTaskAction
            )
            TaskStore.removeTaskStoreListener(TaskEvents.MOVE, handleTaskAction)
        }
    }, [swimlane, props.boardId, props.swimlaneId])

    return (
        <>
            {isDelete && (
                <DeleteSwimlaneModalComponent
                    swimlane={getView()}
                    onCancel={handleDeleteCancel}
                    onConfirm={handleConfirm}
                />
            )}
            {isEdit && (
                <EditSwimlaneComponent
                    swimlane={getView()}
                    onCancel={handleEditCancel}
                    onSave={handleSave}
                />
            )}
            {!isEdit && (
                <SwimlaneComponent
                    boardId={props.boardId}
                    swimlane={getView()}
                    canMove={props.canMove}
                    onEdit={handleEdit}
                    onDelete={handleDeleteRequest}
                    onMove={() => props.onMove(props.boardId, props.swimlaneId)}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                />
            )}
        </>
    )
}

export default ControlSwimlaneComponent
